import { HugeiconsIcon } from "@hugeicons/react";
import { AlertCircleIcon, BellIcon, Cancel01Icon, CancelCircleIcon, CheckmarkCircle01Icon, InformationCircleIcon } from "@hugeicons-pro/core-bulk-rounded";

import { useSyncExternalStore } from "react";

import { cn } from "@/lib/utils";

/**
 * Toasts — the house card at toast size (ticketbalie pattern, Prado green):
 * one hairline border, a white row with a tinted icon plate and the
 * heading-face title, and — when there is a description — the gray
 * `band-footer` underneath that the white row tucks over.
 *
 * Phones: full-width stack under the status bar. Tablet/desktop:
 * bottom-right stack, newest nearest the corner. Errors stay longer so they
 * can actually be read.
 *
 *   import { toast } from "@/components/ui/toaster";
 *   toast.error("Inloggen mislukt", "Controleer je wachtwoord en probeer opnieuw.");
 *   toast.success("Opgeslagen", "Je wijzigingen zijn bewaard.");
 *
 * Mount `<Toaster />` once in `App`. Dependency-free (no sonner/radix).
 */

export type ToastTone = "success" | "error" | "warning" | "info" | "default";

interface ToastItem {
  id: number;
  title: React.ReactNode;
  description?: React.ReactNode;
  tone: ToastTone;
}

interface ToastOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  tone?: ToastTone;
  /** ms on screen. Defaults 5000 (8000 for errors). */
  duration?: number;
}

const DEFAULT_DURATION = 5000;
const ERROR_DURATION = 8000;

let nextId = 1;
let items: ToastItem[] = [];
const listeners = new Set<() => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function getSnapshot(): ToastItem[] {
  return items;
}

export function dismissToast(id: number) {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  if (items.some((t) => t.id === id)) {
    items = items.filter((t) => t.id !== id);
    emit();
  }
}

export function toast(options: ToastOptions): number;
export function toast(title: React.ReactNode, description?: React.ReactNode): number;
export function toast(
  titleOrOptions: ToastOptions | React.ReactNode,
  description?: React.ReactNode,
): number {
  const opts: ToastOptions =
    typeof titleOrOptions === "object" && titleOrOptions !== null && "title" in titleOrOptions
      ? (titleOrOptions as ToastOptions)
      : { title: titleOrOptions as React.ReactNode, description };
  const tone = opts.tone ?? "default";
  const id = nextId++;
  items = [...items.slice(-4), { id, title: opts.title, description: opts.description, tone }];
  emit();
  timers.set(
    id,
    setTimeout(
      () => dismissToast(id),
      opts.duration ?? (tone === "error" ? ERROR_DURATION : DEFAULT_DURATION),
    ),
  );
  return id;
}

toast.success = (title: React.ReactNode, description?: React.ReactNode) =>
  toast({ title, description, tone: "success" });
toast.error = (title: React.ReactNode, description?: React.ReactNode) =>
  toast({ title, description, tone: "error" });
toast.warning = (title: React.ReactNode, description?: React.ReactNode) =>
  toast({ title, description, tone: "warning" });
toast.info = (title: React.ReactNode, description?: React.ReactNode) =>
  toast({ title, description, tone: "info" });

const TONES: Record<ToastTone, { icon: typeof InformationCircleIcon; plate: string }> = {
  success: { icon: CheckmarkCircle01Icon, plate: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  error: { icon: CancelCircleIcon, plate: "bg-red-500/10 text-red-600 dark:text-red-400" },
  warning: { icon: AlertCircleIcon, plate: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  info: { icon: InformationCircleIcon, plate: "bg-primary/10 text-primary" },
  default: { icon: BellIcon, plate: "bg-muted text-foreground/70" },
};

function ToastCard({ item }: { item: ToastItem }) {
  const meta = TONES[item.tone];
  const Icon = meta.icon;
  return (
    <div
      role={item.tone === "error" ? "alert" : "status"}
      className="toast-in group pointer-events-auto relative w-full overflow-hidden rounded-xl border border-border/60 bg-card text-foreground shadow-[0_12px_40px_rgba(0,0,0,0.12)]"
    >
      {/* White row — icon plate + title. Tucks over the gray band below. */}
      <div className="relative z-10 flex items-center gap-3 bg-card p-4 pr-11">
        <span
          aria-hidden
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
            meta.plate,
          )}
        >
          <HugeiconsIcon icon={Icon} size={18} />
        </span>
        <p className="font-heading text-sm font-semibold leading-snug tracking-tight">
          {item.title}
        </p>
      </div>
      {item.description && (
        <div className="band-footer px-4 pb-3 pt-3 text-xs leading-relaxed text-muted-foreground">
          {item.description}
        </div>
      )}
      <button
        type="button"
        onClick={() => dismissToast(item.id)}
        aria-label="Melding sluiten"
        className="absolute right-2 top-2 z-20 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <HugeiconsIcon icon={Cancel01Icon} size={14} />
      </button>
    </div>
  );
}

export function Toaster() {
  const list = useSyncExternalStore(subscribe, getSnapshot);
  if (list.length === 0) return null;
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-[env(safe-area-inset-top,0px)] z-[100] flex w-full flex-col gap-2 p-3 outline-none md:inset-x-auto md:bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] md:right-4 md:top-auto md:w-[380px] md:flex-col-reverse md:p-0"
    >
      {list.map((item) => (
        <ToastCard key={item.id} item={item} />
      ))}
    </div>
  );
}
